/** The words for a decision constraint row, one table for the 422 text (api) and the Decide decision line (web): a source id never reaches a player. */
import { REGULATOR_SOURCE_NAME, type DecisionConstraint } from "@bsim/domain";

type Row = Pick<DecisionConstraint, "source" | "sourceRef">;

const SENTENCE = {
  ...REGULATOR_SOURCE_NAME,
  activist: "the activist's agreement",
  dealCovenant: "the deal covenant",
} as const satisfies Record<Exclude<DecisionConstraint["source"], "event">, string>;
const SHORT = { mou: "regulator's MOU", consentOrder: "regulator's consent order", pca: "regulator's PCA", activist: "activist agreement", dealCovenant: "deal covenant" } as const satisfies Record<keyof typeof SENTENCE, string>;

/** "EV-02:D" → ["EV-02", "D"]. */
const cardChoice = (ref: string | undefined): [string, string] | undefined => {
  const [card, choice] = (ref ?? "").split(":");
  return card === undefined || choice === undefined || card === "" ? undefined : [card, choice];
};

/** Who set the limit, in a sentence: "the regulator's memorandum of understanding (MOU)", "your answer to EV-02 (option C)". */
export function constraintSource(c: Row): string {
  if (c.source !== "event") return SENTENCE[c.source];
  const at = cardChoice(c.sourceRef);
  return at === undefined ? "your answer to an event card" : `your answer to ${at[0]} (option ${at[1]})`;
}

/** Who set the limit, for a decision line: "regulator's MOU", "EV-02:C". */
export const constraintSourceShort = (c: Row): string => (c.source === "event" ? c.sourceRef ?? "event card" : SHORT[c.source]);

/** The decision line for one row: "Capped at Trend · regulator's MOU", "Held at Runoff · EV-02:C". `valueLabel` is the option's own label. */
export function constraintLine(c: Pick<DecisionConstraint, "kind" | "source" | "sourceRef">, valueLabel: string): string {
  const what = c.kind === "max" ? `Capped at ${valueLabel}` : c.kind === "min" ? `At least ${valueLabel}` : c.kind === "locked" ? "Locked" : `Held at ${valueLabel}`;
  return `${what} · ${constraintSourceShort(c)}`;
}
